// Drive calls the home screen makes (identical in Docs, Sheets and Slides).
// The editor's own load / save calls stay in @/api/driveApi.

import { DriveApiError, type DriveItem } from "@/api/driveApi";
import { normalizeCatalogue, type HomeItem, type RawSuiteTemplate, type SuiteCatalogue } from "./homeModel";

const baseUrl = import.meta.env.VITE_DRIVE_BFF_BASE_URL as string;

interface SharedWithMeRow {
    item: DriveItem;
    permissionId: string;
    sharedBy: string;
    sharedAt: string;
    source: string | null;
}

async function failure(res: Response, what: string): Promise<DriveApiError> {
    let message = `${what} → ${res.status} ${res.statusText}`;
    try {
        const body = (await res.json()) as { title?: string; detail?: string };
        message = body.detail ?? body.title ?? message;
    } catch {
        /* not a JSON body — keep the status line */
    }
    return new DriveApiError(message, res.status);
}

async function call(
    path: string,
    token: string | undefined,
    what: string,
    init: { method?: string; json?: unknown; query?: Record<string, string> } = {}
): Promise<Response> {
    const url = new URL(path, baseUrl);
    for (const [key, value] of Object.entries(init.query ?? {})) url.searchParams.set(key, value);
    const headers = new Headers({ Accept: "application/json" });
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (init.json !== undefined) headers.set("Content-Type", "application/json");
    const res = await fetch(url.toString(), {
        method: init.method ?? "GET",
        headers,
        body: init.json !== undefined ? JSON.stringify(init.json) : undefined,
        credentials: "omit"
    });
    if (!res.ok) throw await failure(res, what);
    return res;
}

async function json<T>(res: Response): Promise<T> {
    return (await res.json()) as T;
}

const itemPath = (id: string) => `/api/items/${encodeURIComponent(id)}`;

export const homeApi = {
    /** The user's own files, most recently opened first (max 100). */
    recent: async (take: number, token: string | undefined) =>
        json<HomeItem[]>(await call("/api/recent", token, "Drive recent", { query: { take: String(take) } })),

    favorites: async (token: string | undefined) =>
        json<HomeItem[]>(await call("/api/favorites", token, "Drive favourites")),

    sharedWithMe: async (token: string | undefined): Promise<HomeItem[]> => {
        const rows = await json<SharedWithMeRow[]>(await call("/api/shared-with-me", token, "Drive shared with me"));
        return rows.map((row) => ({ ...row.item, sharedBy: row.sharedBy, sharedAt: row.sharedAt }));
    },

    /** Name search across the user's own files (Drive search doesn't cover shared items). */
    search: async (q: string, take: number, token: string | undefined) =>
        json<HomeItem[]>(await call("/api/search", token, "Drive search", { query: { q, take: String(take) } })),

    listFolder: async (parentId: string | null, token: string | undefined) =>
        json<HomeItem[]>(await call("/api/items", token, "Drive items", { query: parentId ? { parentId } : {} })),

    setFavorite: async (id: string, favorite: boolean, token: string | undefined): Promise<void> => {
        await call(`${itemPath(id)}/${favorite ? "favorite" : "unfavorite"}`, token, "Drive star", { method: "POST" });
    },

    rename: async (id: string, name: string, token: string | undefined) =>
        json<HomeItem>(await call(itemPath(id), token, "Drive rename", { method: "PUT", json: { name } })),

    trash: async (id: string, token: string | undefined): Promise<void> => {
        await call(`${itemPath(id)}/trash`, token, "Drive move to bin", { method: "POST" });
    },

    /** "Copy of X" in the same folder (someone else's file copies to My Drive). */
    copy: async (id: string, token: string | undefined) =>
        json<HomeItem>(await call(`${itemPath(id)}/copy`, token, "Drive copy", { method: "POST", json: {} })),

    /** The suite's shared template library — every app's entries; filter by `app`. */
    templates: async (token: string | undefined): Promise<SuiteCatalogue> =>
        normalizeCatalogue(
            await json<{ templates?: RawSuiteTemplate[]; industries?: unknown } | null>(await call("/api/templates", token, "Drive templates"))
        ),

    /** Copies a template into the root of My Drive (Drive picks a unique name) and returns the new file. */
    copyTemplate: async (id: string, token: string | undefined) =>
        json<HomeItem>(await call(`/api/templates/${encodeURIComponent(id)}/copy`, token, "Create from template", { method: "POST", json: {} })),

    /** Image files only; 404 when Drive hasn't made one. */
    thumbnail: async (id: string, token: string | undefined): Promise<Blob> => {
        const res = await call(`/api/files/${encodeURIComponent(id)}/thumbnail`, token, "Drive thumbnail");
        return await res.blob();
    }
};
